import { supabase } from './client';

export type UserRole = 'admin' | 'moderator' | 'seller' | 'partner';
export type VerificationStatus = 'submitted' | 'pending' | 'verified' | 'rejected' | 'expired';

/**
 * Check if a user has a specific role
 */
export const hasRole = async (userId: string, role: UserRole): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('user_roles')
      .select('id')
      .eq('user_id', userId)
      .eq('role', role)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows found
        return false;
      }
      console.error('Error checking role:', error);
      return false;
    }

    return !!data;
  } catch (err) {
    console.error('Error in hasRole:', err);
    return false;
  }
};

/**
 * Check if current user is admin
 */
export const isCurrentUserAdmin = async (): Promise<boolean> => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    
    if (authError || !user) {
      return false;
    }

    return hasRole(user.id, 'admin');
  } catch (err) {
    console.error('Error in isCurrentUserAdmin:', err);
    return false;
  }
};

/**
 * Get all roles for a user
 */
export const getUserRoles = async (userId: string): Promise<UserRole[]> => {
  try {
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId);

    if (error) {
      console.error('Error fetching user roles:', error);
      return [];
    }

    return data?.map(r => r.role as UserRole) || [];
  } catch (err) {
    console.error('Error in getUserRoles:', err);
    return [];
  }
};

/**
 * Add a role to a user (admin only)
 */
export const addRoleToUser = async (userId: string, role: UserRole): Promise<boolean> => {
  try {
    const isAdmin = await isCurrentUserAdmin();
    if (!isAdmin) {
      console.error('Only admins can add roles');
      return false;
    }

    const { error } = await supabase
      .from('user_roles')
      .insert([
        {
          user_id: userId,
          role,
          created_by: (await supabase.auth.getUser()).data.user?.id,
        },
      ]);

    if (error) {
      console.error('Error adding role:', error);
      return false;
    }

    return true;
  } catch (err) {
    console.error('Error in addRoleToUser:', err);
    return false;
  }
};

/**
 * Remove a role from a user (admin only)
 */
export const removeRoleFromUser = async (userId: string, role: UserRole): Promise<boolean> => {
  try {
    const isAdmin = await isCurrentUserAdmin();
    if (!isAdmin) {
      console.error('Only admins can remove roles');
      return false;
    }

    const { error } = await supabase
      .from('user_roles')
      .delete()
      .eq('user_id', userId)
      .eq('role', role);

    if (error) {
      console.error('Error removing role:', error);
      return false;
    }

    return true;
  } catch (err) {
    console.error('Error in removeRoleFromUser:', err);
    return false;
  }
};

/**
 * Get pending verifications (admin only)
 */
export const getPendingVerifications = async () => {
  try {
    const isAdmin = await isCurrentUserAdmin();
    if (!isAdmin) {
      console.error('Only admins can view pending verifications');
      return [];
    }

    const { data, error } = await supabase
      .from('profiles')
      .select(`
        id,
        username,
        display_name,
        avatar_url,
        verification_status,
        verification_photos,
        verified_by,
        verified_at
      `)
      .in('verification_status', ['pending', 'submitted'])
      .order('verified_at', { ascending: true, nullsFirst: true });

    if (error) {
      console.error('Error fetching pending verifications:', error);
      return [];
    }

    return data || [];
  } catch (err) {
    console.error('Error in getPendingVerifications:', err);
    return [];
  }
};

/**
 * Get all verifications (admin only)
 */
export const getAllVerifications = async () => {
  try {
    const isAdmin = await isCurrentUserAdmin();
    
    if (!isAdmin) {
      console.error('Only admins can view verifications');
      return [];
    }

    // Test: Try to fetch ALL profiles first
    const { data: allProfiles, error: allError } = await supabase
      .from('profiles')
      .select('id, username, verification_status')
      .limit(100);


    if (allError) {
      console.error('Error fetching all profiles:', allError);
    }

    // Now try with verification data
    const { data, error } = await supabase
      .from('profiles')
      .select(`
        id,
        username,
        display_name,
        avatar_url,
        verification_status,
        verification_photos,
        verified_by,
        verified_at
      `)
      .order('verified_at', { ascending: false, nullsFirst: false })
      .limit(100);


    if (error) {
      console.error('Error fetching verifications:', error);
      return [];
    }

    return data || [];
  } catch (err) {
    console.error('Error in getAllVerifications:', err);
    return [];
  }
};

/**
 * Update verification status (admin only)
 */
export const updateVerificationStatus = async (
  userId: string,
  status: VerificationStatus
): Promise<boolean> => {
  try {
    const isAdmin = await isCurrentUserAdmin();
    if (!isAdmin) {
      console.error('Only admins can update verification status');
      return false;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('Current user not found');
      return false;
    }

    const { error } = await supabase
      .from('profiles')
      .update({
        verification_status: status,
        verified_by: user.id,
        verified_at: status === 'verified' ? new Date().toISOString() : null,
      })
      .eq('id', userId);

    if (error) {
      console.error('Error updating verification status:', error);
      return false;
    }

    return true;
  } catch (err) {
    console.error('Error in updateVerificationStatus:', err);
    return false;
  }
};

/**
 * Reject verification (admin only)
 */
export const rejectVerification = async (userId: string): Promise<boolean> => {
  return updateVerificationStatus(userId, 'rejected');
};

/**
 * Approve verification (admin only)
 */
export const approveVerification = async (userId: string): Promise<boolean> => {
  return updateVerificationStatus(userId, 'verified');
};

/**
 * Search users by ID, email, or phone (admin only)
 */
export const searchUsers = async (query: string): Promise<any[]> => {
  try {
    const isAdmin = await isCurrentUserAdmin();
    if (!isAdmin) {
      console.error('Only admins can search users');
      return [];
    }

    // Search in auth.users by email (if it looks like email)
    let results: any[] = [];

    // Search by ID
    if (query.length === 36 && query.includes('-')) {
      // UUID format
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username, email, phone_number, display_name, avatar_url')
        .eq('id', query)
        .limit(5);

      if (data) results.push(...data);
    }

    // Search by email
    if (query.includes('@')) {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username, email, phone_number, display_name, avatar_url')
        .ilike('email', `%${query}%`)
        .limit(5);

      if (data) {
        // Avoid duplicates
        results = results.filter(r => !data.find(d => d.id === r.id));
        results.push(...data);
      }
    }

    // Search by phone
    const phoneRegex = /[\d\s\-\+\(\)]/g;
    if (query.match(phoneRegex)) {
      const phoneQuery = query.replace(/\D/g, ''); // Remove non-digits
      if (phoneQuery.length > 0) {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, username, email, phone_number, display_name, avatar_url')
          .ilike('phone_number', `%${phoneQuery}%`)
          .limit(5);

        if (data) {
          // Avoid duplicates
          results = results.filter(r => !data.find(d => d.id === r.id));
          results.push(...data);
        }
      }
    }

    // Search by username
    if (query.length > 2) {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username, email, phone_number, display_name, avatar_url')
        .ilike('username', `%${query}%`)
        .limit(5);

      if (data) {
        // Avoid duplicates
        results = results.filter(r => !data.find(d => d.id === r.id));
        results.push(...data);
      }
    }

    return results.slice(0, 10); // Return max 10 results
  } catch (err) {
    console.error('Error in searchUsers:', err);
    return [];
  }
};

/**
 * Get user roles with user info (admin only)
 */
export const getUserWithRoles = async (userId: string): Promise<any> => {
  try {
    const isAdmin = await isCurrentUserAdmin();
    if (!isAdmin) {
      console.error('Only admins can get user roles');
      return null;
    }

    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('id, username, email, phone_number, display_name, avatar_url')
      .eq('id', userId)
      .single();

    if (profileError || !profile) {
      console.error('Error fetching user profile:', profileError);
      return null;
    }

    const roles = await getUserRoles(userId);

    return {
      ...profile,
      roles,
    };
  } catch (err) {
    console.error('Error in getUserWithRoles:', err);
    return null;
  }
};
